<template>
  <div class="pj" :class="{ 'pj-mobile': mobile }">
    <div v-if="loading" class="pj-state"><b-spinner variant="primary"></b-spinner></div>
    <div v-else-if="error" class="pj-state">
      <h1 class="pj-title">Журнал недоступен</h1>
      <p class="pj-muted">{{ error }}</p>
    </div>

    <template v-else>
      <!-- шапка: предмет, группа, «только просмотр» -->
      <header class="pj-bar">
        <div class="pj-titles">
          <h1 class="pj-title">{{ data.predm }}</h1>
          <div class="pj-sub">{{ data.group }}<span v-if="!mobile && legendOn"> · <span class="pj-att-present">Я</span> был(а), <span class="pj-att-absent">Н</span> не был(а), <span class="pj-att-excused">НУ</span> по уважительной</span></div>
        </div>
        <span class="pj-ro"><b-icon icon="eye" aria-hidden="true"></b-icon>{{ mobile ? 'Просмотр' : 'Только просмотр' }}</span>
      </header>

      <!-- «Найти себя»: на компьютере всегда, на телефоне — в листах «Мои отметки» и «Задания» -->
      <div v-if="!mobile || tab !== 'group'" class="pj-find">
        <label for="pj-me" class="pj-find-label">{{ mobile ? 'Я —' : 'Найти себя' }}</label>
        <select id="pj-me" v-model.number="me" class="pj-select" @change="meChanged">
          <option :value="-1">— выберите себя в списке —</option>
          <option v-for="(s, i) in data.students" :key="i" :value="i">{{ s.name }}</option>
        </select>
      </div>

      <!-- строка выбранной ячейки (как строка формул, только по-человечески) -->
      <div v-if="!mobile || tab === 'group'" class="pj-cellinfo" aria-live="polite">
        <template v-if="selInfo"><b>{{ selInfo.name }}</b> · {{ selInfo.lesson }} — {{ selInfo.mark }}</template>
        <span v-else class="pj-muted">{{ mobile ? 'Нажмите на ячейку — здесь будет отметка целиком' : 'Выберите ячейку — здесь будет отметка целиком' }}</span>
      </div>

      <p v-if="!lessons.length" class="pj-empty">Проведённых занятий пока нет.</p>

      <div v-else class="pj-main">
        <!-- ЛИСТ «Мои отметки» (телефон) -->
        <section v-if="mobile && tab === 'me'" class="pj-feed" aria-label="Мои отметки">
          <p v-if="me < 0" class="pj-hint">Выберите себя в списке выше — здесь появятся ваши отметки и задания по каждому занятию.</p>
          <template v-else>
            <button v-if="lastNote" type="button" class="pj-last" @click="tab = 'notes'">
              <span class="pj-last-head"><b-icon icon="journal-text" aria-hidden="true"></b-icon> Последнее задание · {{ shortDate(lastNote.day) }}</span>
              <span class="pj-last-text">{{ lastNote.note }}</span>
              <span class="pj-last-more">Все задания ({{ notes.length }}) <b-icon icon="chevron-right" aria-hidden="true"></b-icon></span>
            </button>
            <div class="pj-chips">
              <template v-if="on('attendance')">
                <span>Пропуски <b>{{ sum(me).absent }}</b></span>
                <span>Уваж. <b>{{ sum(me).excused }}</b></span>
              </template>
              <span v-if="on('grades') && sum(me).avg">Ср. балл <b>{{ sum(me).avg }}</b></span>
              <span v-if="on('credit') && sum(me).zach">Зачёты <b>{{ sum(me).zach }}</b></span>
              <span v-if="on('points') && sum(me).points !== ''">Баллы <b>{{ sum(me).points }}</b></span>
            </div>
            <article v-for="l in lessons" :key="l.day + l.time" class="pj-card">
              <div class="pj-card-head">
                <div><b>{{ shortDate(l.day) }}</b> <span class="pj-muted">{{ typeLine(l) }}</span></div>
                <div class="pj-card-mark"><mark-view :m="cell(me, l)" /><span v-if="!cell(me, l)" class="pj-muted">—</span></div>
              </div>
              <div v-if="l.note" class="pj-card-note">
                <span class="pj-note-label"><b-icon icon="journal-text" aria-hidden="true"></b-icon> Задание / заметка</span>
                <span class="pj-note-text">{{ l.note }}</span>
              </div>
            </article>
          </template>
        </section>

        <!-- ЛИСТ «Задания» (телефон) -->
        <section v-if="mobile && tab === 'notes'" class="pj-feed" aria-label="Задания и заметки">
          <article v-for="l in notes" :key="l.day + l.time" class="pj-note" :class="{ 'pj-note-on': noteOn === key(l) }">
            <div class="pj-note-date">
              <span>{{ fullDate(l.day) }} · {{ typeLine(l) }}</span>
              <span v-if="me >= 0" class="pj-note-mine"><mark-view :m="cell(me, l)" /></span>
            </div>
            <div class="pj-note-text">{{ l.note }}</div>
          </article>
        </section>

        <!-- общая таблица: компьютер всегда, телефон — лист «Вся группа» -->
        <div v-if="!mobile || tab === 'group'" ref="grid" class="pj-grid" tabindex="0" aria-label="Таблица журнала группы">
          <table class="pj-table">
            <thead>
              <tr>
                <th class="pj-name" scope="col">Студент</th>
                <th v-for="l in lessons" :key="key(l)" scope="col" class="pj-lh" :class="{ 'pj-colsel': colOn === key(l), 'pj-hasnote': l.note }"
                  :data-col="key(l)">
                  <button v-if="l.note" type="button" class="pj-lh-btn" :title="'Задание: ' + l.note" @click="openNote(l)">
                    <span class="pj-lh-date">{{ shortDate(l.day) }}<b-icon icon="journal-text" class="pj-lh-ic" aria-label="есть задание"></b-icon></span>
                    <span class="pj-lh-meta">{{ typeLine(l) }}</span>
                  </button>
                  <template v-else>
                    <span class="pj-lh-date">{{ shortDate(l.day) }}</span>
                    <span class="pj-lh-meta">{{ typeLine(l) }}</span>
                  </template>
                </th>
                <template v-if="on('attendance')">
                  <th class="pj-sum" scope="col" title="Неявки">Н</th>
                  <th v-if="!mobile" class="pj-sum" scope="col" title="Неявки по уважительной причине">НУ</th>
                </template>
                <th v-if="on('grades')" class="pj-sum" scope="col" title="Средний балл">Ср.</th>
                <th v-if="on('credit') && !mobile" class="pj-sum" scope="col" title="Зачёты">Зач.</th>
                <th v-if="on('points')" class="pj-sum" scope="col" title="Сумма баллов">Σ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(s, i) in data.students" :key="i" :data-row="i" :class="{ 'pj-me': i === me, 'pj-rowsel': sel && sel.i === i, 'pj-inactive': !s.active }">
                <th class="pj-name" scope="row">{{ mobile ? shortName(s.name) : s.name }}</th>
                <td v-for="l in lessons" :key="key(l)" class="pj-cell" :class="{ 'pj-sel': sel && sel.i === i && sel.k === key(l) }"
                  @click="select(i, l)">
                  <mark-view :m="cell(i, l)" />
                </td>
                <template v-if="on('attendance')">
                  <td class="pj-sum">{{ sum(i).absent || '' }}</td>
                  <td v-if="!mobile" class="pj-sum">{{ sum(i).excused || '' }}</td>
                </template>
                <td v-if="on('grades')" class="pj-sum">{{ sum(i).avg }}</td>
                <td v-if="on('credit') && !mobile" class="pj-sum">{{ sum(i).zach }}</td>
                <td v-if="on('points')" class="pj-sum">{{ sum(i).points }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- панель заданий справа (компьютер) -->
        <aside v-if="!mobile" class="pj-side" aria-label="Задания и заметки к занятиям">
          <h2 class="pj-side-title"><b-icon icon="journal-text" aria-hidden="true"></b-icon> Задания и заметки <span class="pj-muted">{{ notes.length }}</span></h2>
          <div ref="side" class="pj-side-list">
            <p v-if="!notes.length" class="pj-muted pj-side-empty">Преподаватель пока не оставил заметок к занятиям.</p>
            <button v-for="l in notes" :key="key(l)" type="button" class="pj-note" :class="{ 'pj-note-on': noteOn === key(l) }"
              :data-note="key(l)" @click="focusColumn(l)">
              <span class="pj-note-date">
                <span>{{ fullDate(l.day) }} · {{ typeLine(l) }}</span>
                <span v-if="me >= 0" class="pj-note-mine"><mark-view :m="cell(me, l)" /></span>
              </span>
              <span class="pj-note-text">{{ l.note }}</span>
            </button>
          </div>
        </aside>
      </div>

      <!-- листы (телефон) -->
      <nav v-if="mobile && lessons.length" class="pj-tabs" role="tablist" aria-label="Листы журнала">
        <button type="button" role="tab" class="pj-tab" :aria-selected="String(tab === 'me')" @click="tab = 'me'">
          <b-icon icon="person" aria-hidden="true"></b-icon>Мои отметки
        </button>
        <button v-if="notes.length" type="button" role="tab" class="pj-tab" :aria-selected="String(tab === 'notes')" @click="tab = 'notes'">
          <b-icon icon="journal-text" aria-hidden="true"></b-icon>Задания<span class="pj-badge">{{ notes.length }}</span>
        </button>
        <button type="button" role="tab" class="pj-tab" :aria-selected="String(tab === 'group')" @click="tab = 'group'">
          <b-icon icon="people" aria-hidden="true"></b-icon>Вся группа
        </button>
      </nav>

      <!-- заметка занятия поверх таблицы (телефон: нажатие на дату) -->
      <div v-if="sheet" class="pj-sheet-back" @click.self="sheet = null">
        <div class="pj-sheet" role="dialog" aria-modal="true" :aria-label="'Задание ' + fullDate(sheet.day)">
          <div class="pj-note-date"><span>{{ fullDate(sheet.day) }} · {{ typeLine(sheet) }}</span></div>
          <div class="pj-note-text">{{ sheet.note }}</div>
          <div class="pj-sheet-actions">
            <button type="button" class="pj-btn" @click="sheet = null; tab = 'notes'">Все задания</button>
            <button type="button" class="pj-btn pj-btn-primary" @click="sheet = null">Закрыть</button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
const PATH = process.env.VUE_APP_PATH || ''
const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб']
const ATT = { present: 'Я', absent: 'Н', excused: 'НУ' }
const MOBILE_MQ = '(max-width: 899.98px)'

// отметка ячейки: явка, оценка, баллы
const markView = {
  functional: true,
  props: { m: Object },
  render(h, { props }) {
    const m = props.m
    if (!m) return null
    const out = []
    if (m.attendance) out.push(h('span', { class: 'pj-att-' + m.attendance }, ATT[m.attendance]))
    if (m.grade) out.push(h('b', { class: 'pj-grade' }, m.grade))
    if (m.points != null) out.push(h('span', { class: 'pj-points' }, String(m.points)))
    return out
  },
}

// Публичный просмотр журнала по ссылке ?j=<token> (кнопка «Поделиться» в журнале).
// Без авторизации и без редактирования: данные — GET /api/journal/public/:token.
// Компьютер: таблица на весь экран + «Найти себя» + панель заданий справа.
// Телефон: листы «Мои отметки» (лента своих занятий с заданиями), «Задания», «Вся группа».
export default {
  name: 'publicJournal',
  components: { markView },
  props: { token: String },
  data() {
    return {
      loading: true, error: '', data: null,
      me: -1, sel: null, colOn: '', noteOn: '', sheet: null,
      mobile: window.matchMedia(MOBILE_MQ).matches,
      tab: 'me',
    }
  },
  computed: {
    lessons() {
      return this.data ? this.data.lessons : []
    },
    notes() {
      return this.lessons.filter(l => l.note)
    },
    lastNote() {
      return this.notes[0] || null
    },
    legendOn() {
      return this.on('attendance')
    },
    marks() {
      const map = {}
      ;(this.data ? this.data.marks : []).forEach(m => { map[m.s + '|' + m.day + '|' + m.time] = m })
      return map
    },
    selInfo() {
      if (!this.sel) return null
      const l = this.lessons.find(x => this.key(x) === this.sel.k)
      const m = this.cell(this.sel.i, l) || {}
      const parts = [ATT[m.attendance] && { present: 'был(а)', absent: 'не был(а)', excused: 'не был(а) по уважительной причине' }[m.attendance],
        m.grade && 'оценка ' + m.grade, m.points != null && m.points + ' баллов'].filter(Boolean)
      return {
        name: this.data.students[this.sel.i].name,
        lesson: `${this.fullDate(l.day)}, ${l.type || ''} ${l.time.split('-')[0]}`,
        mark: parts.length ? parts.join(', ') : 'нет отметки',
      }
    },
  },
  async mounted() {
    document.title = 'Журнал — просмотр'
    // страница с ФИО студентов — не для поисковиков
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex, nofollow'
    document.head.appendChild(meta)
    this.mq = window.matchMedia(MOBILE_MQ)
    this.onMq = e => { this.mobile = e.matches }
    this.mq.addEventListener('change', this.onMq)
    try {
      const { data } = await this.axios.get(PATH + '/api/journal/public/' + encodeURIComponent(this.token))
      this.data = data
      document.title = `${data.predm} · ${data.group} — журнал`
      // выбор «Я —» помнится в этом браузере (по ссылке)
      let saved = -1
      try { saved = Number(localStorage['pjMe_' + this.token] ?? -1) } catch (e) { /* без памяти */ }
      if (saved >= 0 && saved < data.students.length) this.me = saved
      this.tab = this.me >= 0 ? 'me' : (data.lessons.some(l => l.note) ? 'notes' : 'group')
      if (this.me >= 0 && !this.mobile) this.$nextTick(() => this.scrollToRow(this.me))
    } catch (e) {
      this.error = (e.response && e.response.data && e.response.data.error) || 'Не удалось загрузить журнал. Попробуйте обновить страницу.'
    } finally {
      this.loading = false
    }
  },
  beforeDestroy() {
    if (this.mq) this.mq.removeEventListener('change', this.onMq)
  },
  methods: {
    on(mode) {
      return !!this.data && this.data.modes.includes(mode)
    },
    key(l) {
      return l.day + '|' + l.time
    },
    cell(i, l) {
      return (l && this.marks[i + '|' + l.day + '|' + l.time]) || null
    },
    meChanged() {
      try { localStorage['pjMe_' + this.token] = String(this.me) } catch (e) { /* без памяти */ }
      if (this.me >= 0) {
        if (this.mobile) this.tab = 'me'
        else this.$nextTick(() => this.scrollToRow(this.me))
      }
    },
    scrollToRow(i) {
      const tr = this.$refs.grid && this.$refs.grid.querySelector(`tr[data-row="${i}"]`)
      if (tr) tr.scrollIntoView({ block: 'center', behavior: 'smooth' })
    },
    select(i, l) {
      this.sel = { i, k: this.key(l) }
    },
    // клик по дате: компьютер — подсветить задание в панели, телефон — открыть его снизу
    openNote(l) {
      if (this.mobile) { this.sheet = l; return }
      this.colOn = this.noteOn = this.key(l)
      const el = this.$refs.side && this.$refs.side.querySelector(`[data-note="${this.key(l)}"]`)
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    },
    // клик по заданию в панели — подсветить и показать его столбец
    focusColumn(l) {
      this.colOn = this.noteOn = this.key(l)
      const th = this.$refs.grid && this.$refs.grid.querySelector(`th[data-col="${this.key(l)}"]`)
      if (th) th.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
    },
    sum(i) {
      let absent = 0, excused = 0, s = 0, n = 0, zach = 0, zachTotal = 0, pts = 0, ptsN = 0
      this.lessons.forEach(l => {
        const v = this.cell(i, l)
        if (!v) return
        if (v.attendance === 'absent') absent++
        if (v.attendance === 'excused') excused++
        if (/^[2-5]$/.test(v.grade || '')) { s += Number(v.grade); n++ }
        if (v.grade === 'зач') { zach++; zachTotal++ }
        if (v.grade === 'незач') zachTotal++
        if (v.points != null) { pts += v.points; ptsN++ }
      })
      return { absent, excused, avg: n ? (s / n).toFixed(1).replace('.', ',') : '', zach: zachTotal ? `${zach}/${zachTotal}` : '', points: ptsN ? pts : '' }
    },
    shortName(name) {
      return name.split(' ').slice(0, 2).join(' ')
    },
    shortDate(day) {
      const [y, m, d] = day.split('-')
      return `${WEEKDAYS[new Date(Number(y), Number(m) - 1, Number(d)).getDay()]} ${d}.${m}`
    },
    fullDate(day) {
      const [y, m, d] = day.split('-')
      return `${WEEKDAYS[new Date(Number(y), Number(m) - 1, Number(d)).getDay()]} ${d}.${m}.${y}`
    },
    // "Лаб 8:20 · 1 подгр." — занятие у подгруппы (из расписания кафедры)
    typeLine(l) {
      const nums = [...new Set(String(l.subgroups || '').match(/\d+(?= подгруппа)/g) || [])]
      return `${this.typeAbbr(l.type)} ${l.time.split('-')[0]}${nums.length ? ' · ' + nums.join(', ') + ' подгр.' : ''}`
    },
    typeAbbr(type) {
      const t = (type || '').toLowerCase()
      if (t.startsWith('лек')) return 'Лек'
      if (t.startsWith('практ')) return 'Пр'
      if (t.startsWith('лаб')) return 'Лаб'
      return (type || '').slice(0, 3)
    },
  },
}
</script>

<style>
/* Табличный вид в палитре сайта: из электронных таблиц взяты плотная сетка 1px,
   закреплённые шапка и столбец ФИО, выделение ячейки рамкой, строка выбранной
   ячейки, листы внизу на телефоне. Страница занимает весь экран. */
.pj {
  --pj-grid: var(--border);
  --pj-grid-strong: #b8cdc8;
  --pj-sel-fill: rgba(13, 148, 136, .08);
  --pj-note-bg: #fef7e0;
  --pj-note-line: #f59e0b;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--bg);
  color: var(--text);
  font-size: 14px;
}
@media (prefers-color-scheme: dark) {
  .pj {
    --pj-grid-strong: #33584f;
    --pj-sel-fill: rgba(45, 212, 191, .10);
    --pj-note-bg: #33301c;
    --pj-note-line: #fbbf24;
  }
}
.pj-state { margin: auto; padding: 40px 16px; text-align: center; }
.pj-muted { color: var(--text-muted); }
.pj-empty { padding: 24px 16px; color: var(--text-muted); }

.pj-bar {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--pj-grid);
}
.pj-titles { min-width: 0; flex: 1; }
.pj-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pj-sub { font-size: 13px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pj-sub > :first-child { color: inherit; }
.pj-ro {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 14px;
  background: var(--item-hover);
  color: var(--primary);
  font-size: 12px;
  font-weight: 600;
}

/* строка «Найти себя»: как поле в строке меню таблицы — подпись-ячейка
   слева, список на всю ширину, без отступов и скруглений */
.pj-find {
  flex: none;
  display: flex;
  align-items: stretch;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--pj-grid);
}
.pj-find-label {
  flex: none;
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0 12px;
  font-size: 12px;
  color: var(--text-muted);
  white-space: nowrap;
  background: var(--bg-muted);
  border-right: 1px solid var(--pj-grid);
}
.pj-select {
  flex: 1;
  min-width: 0;
  height: 34px;
  padding: 0 32px 0 12px;
  font: inherit;
  color: var(--text);
  background-color: var(--bg-elevated);
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%238c9b96' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  border: 0;
  border-radius: 0;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
}
.pj-select:hover { background-color: var(--item-hover); }
.pj-select:focus-visible { outline: 2px solid var(--primary); outline-offset: -2px; }
.pj-grid:focus-visible, .pj-note:focus-visible, .pj-tab:focus-visible, .pj-lh-btn:focus-visible, .pj-last:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 1px;
}
.pj-cellinfo {
  flex: none;
  padding: 5px 16px;
  min-height: 30px;
  font-size: 13px;
  border-bottom: 1px solid var(--pj-grid);
  background: var(--bg-elevated);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pj-main { flex: 1; min-height: 0; display: flex; }
.pj-grid { flex: 1; min-width: 0; overflow: auto; overscroll-behavior: contain; background: var(--bg-elevated); }

.pj-table { border-collapse: separate; border-spacing: 0; font-size: 13px; font-variant-numeric: tabular-nums; }
.pj-table th, .pj-table td {
  height: 28px;
  padding: 0 8px;
  border-right: 1px solid var(--pj-grid);
  border-bottom: 1px solid var(--pj-grid);
  background: var(--bg-elevated);
  white-space: nowrap;
  text-align: center;
}
.pj-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  height: 42px;
  background: var(--bg-muted);
  font-weight: 600;
  line-height: 1.15;
  border-bottom: 2px solid var(--pj-grid-strong);
}
.pj-table .pj-name {
  position: sticky;
  left: 0;
  z-index: 1;
  min-width: 210px;
  text-align: left;
  font-weight: 500;
  border-right: 2px solid var(--pj-grid-strong);
}
.pj-table thead .pj-name { z-index: 3; }
.pj-lh { min-width: 74px; position: relative; }
.pj-lh-date, .pj-lh-meta { display: block; }
.pj-lh-meta { font-size: 11px; font-weight: 400; color: var(--text-muted); }
.pj-lh-btn {
  border: 0;
  padding: 0;
  background: none;
  font: inherit;
  color: var(--primary);
  cursor: pointer;
  line-height: 1.15;
}
.pj-lh-ic { margin-left: 3px; font-size: 12px; vertical-align: -1px; }
/* уголок-метка, как у ячейки с комментарием */
.pj-hasnote::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  border-style: solid;
  border-width: 0 7px 7px 0;
  border-color: transparent var(--pj-note-line) transparent transparent;
}
.pj-colsel { background: var(--item-hover) !important; }
.pj-cell { min-width: 74px; cursor: cell; }
.pj-sel { box-shadow: inset 0 0 0 2px var(--primary); background: var(--pj-sel-fill) !important; }
.pj-rowsel td, .pj-rowsel th { background: var(--pj-sel-fill); }
.pj-me td, .pj-me th { background: var(--pj-sel-fill); }
.pj-me .pj-name { font-weight: 700; box-shadow: inset 3px 0 0 var(--primary); }
.pj-sum { min-width: 46px; font-weight: 600; }
.pj-table td.pj-sum { background: var(--bg-muted); text-align: right; }
.pj-inactive .pj-name { color: var(--text-muted); }
.pj-att-present { color: var(--success); font-weight: 600; }
.pj-att-absent { color: var(--danger); font-weight: 600; }
.pj-att-excused { color: var(--warning); font-weight: 600; }
.pj-grade { margin-left: 4px; }
.pj-points { margin-left: 4px; font-weight: 600; color: var(--primary); }

/* панель заданий справа */
.pj-side {
  flex: none;
  width: 330px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-left: 1px solid var(--pj-grid);
  background: var(--bg);
}
.pj-side-title {
  flex: none;
  margin: 0;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid var(--pj-grid);
  display: flex;
  align-items: center;
  gap: 6px;
}
.pj-side-list { overflow: auto; overscroll-behavior: contain; padding: 10px; display: grid; gap: 8px; align-content: start; }
.pj-side-empty { padding: 4px; }
.pj-note {
  display: block;
  width: 100%;
  text-align: left;
  font: inherit;
  color: var(--text);
  background: var(--bg-elevated);
  border: 1px solid var(--pj-grid);
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
}
.pj-note:hover { border-color: var(--pj-grid-strong); }
.pj-note-on { border-color: var(--primary); box-shadow: 0 0 0 1px var(--primary); }
.pj-note-date {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: var(--text-muted);
  margin-bottom: 4px;
}
.pj-note-mine { white-space: nowrap; }
.pj-note-text { display: block; white-space: pre-wrap; overflow-wrap: anywhere; line-height: 1.45; }

/* ---------- телефон ---------- */
.pj-mobile .pj-bar { padding: 8px 12px; }
.pj-mobile .pj-title { font-size: 16px; }
.pj-mobile .pj-cellinfo { padding-left: 12px; padding-right: 12px; }
.pj-mobile .pj-table .pj-name { min-width: 124px; max-width: 124px; white-space: normal; line-height: 1.2; padding: 4px 6px; }
.pj-mobile .pj-cell, .pj-mobile .pj-lh { min-width: 60px; }
.pj-mobile .pj-hasnote::after { display: none; }

.pj-feed {
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  padding: 12px;
  display: grid;
  gap: 8px;
  align-content: start;
}
.pj-hint { color: var(--text-muted); text-align: center; padding: 24px 8px; margin: 0; }
.pj-last {
  display: grid;
  gap: 4px;
  text-align: left;
  font: inherit;
  color: var(--text);
  background: var(--pj-note-bg);
  border: 1px solid var(--pj-note-line);
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
}
.pj-last-head { font-size: 12px; font-weight: 600; display: flex; align-items: center; gap: 6px; }
.pj-last-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
  line-height: 1.4;
}
.pj-last-more { font-size: 13px; font-weight: 600; color: var(--primary); display: flex; align-items: center; gap: 2px; }
.pj-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.pj-chips span {
  padding: 3px 10px;
  border-radius: 14px;
  font-size: 12px;
  background: var(--bg-elevated);
  border: 1px solid var(--pj-grid);
}
.pj-card {
  background: var(--bg-elevated);
  border: 1px solid var(--pj-grid);
  border-radius: 10px;
  padding: 10px 12px;
}
.pj-card-head { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.pj-card-mark { font-size: 15px; white-space: nowrap; }
.pj-card-note {
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--pj-note-bg);
  overflow-wrap: anywhere;
  line-height: 1.4;
  font-size: 13px;
}
.pj-note-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: var(--text-muted);
  margin-bottom: 3px;
  white-space: normal;
}

.pj-tabs {
  flex: none;
  display: flex;
  border-top: 1px solid var(--pj-grid);
  background: var(--bg-elevated);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
.pj-tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-height: 54px;
  padding: 6px 4px;
  border: 0;
  background: none;
  font: inherit;
  font-size: 12px;
  color: var(--text-muted);
  cursor: pointer;
  position: relative;
}
.pj-tab svg { font-size: 20px; }
.pj-tab[aria-selected="true"] { color: var(--primary); font-weight: 600; box-shadow: inset 0 3px 0 var(--primary); }
.pj-badge {
  position: absolute;
  top: 4px;
  left: calc(50% + 8px);
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--pj-note-line);
  color: #1c1300;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
}

.pj-sheet-back {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: var(--scrim);
  display: flex;
  align-items: flex-end;
}
.pj-sheet {
  width: 100%;
  max-height: 70vh;
  overflow: auto;
  background: var(--bg-elevated);
  border-radius: 16px 16px 0 0;
  padding: 16px 16px calc(16px + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -6px 24px var(--shadow);
}
.pj-sheet-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }
.pj-btn {
  min-height: 40px;
  padding: 0 14px;
  font: inherit;
  border-radius: 8px;
  border: 1px solid var(--pj-grid-strong);
  background: var(--bg-elevated);
  color: var(--text);
}
.pj-btn-primary { background: var(--primary); border-color: var(--primary); color: var(--on-primary); }
</style>
